import { useSyncExternalStore } from "react"
import type { LexicalContent } from "@/lib/cms"

export interface CartItem {
  /** For intl: the message key. For cms: see `cmsCartKey` below. */
  key: string
  source: "intl" | "cms"
  page: string
  values: Record<string, { old: string; new: string }> // locale → edit
  /** Present iff source === "cms"; routes the apply to /api/edit/cms. For a
   *  whole-field Lexical edit `locale` is the write locale (from the provenance,
   *  not the placeholder-`values` key order) and `baseline` is the draft tree the
   *  editor opened on — the apply re-fetches and refuses to write if the live
   *  draft has drifted from it (lost-update protection at whole-tree granularity). */
  cms?: {
    collection: string
    id: string
    field: string
    locale?: string
    baseline?: LexicalContent
  }
  /** Set for a whole-field Lexical (rich-text) edit: the full serialized
   *  `content` tree to draft-write. `values` then carries only a human-readable
   *  placeholder for the cart list; `fullValue` is the real payload. */
  fullValue?: LexicalContent
  /** Set when the edited text was rendered inside an <Experiment> arm — makes
   *  the A/B variant explicit in the cart list and the PR body. */
  experiment?: { flag: string; variant: string }
}

/**
 * Cart key for a CMS edit. A field is one cart entry keyed on
 * `collection:id:field` — scalar, html, and whole-field lexical edits all key
 * the same (one edit per field, last write wins).
 */
export function cmsCartKey(cms: {
  collection: string
  id: string
  field: string
}): string {
  return `${cms.collection}:${cms.id}:${cms.field}`
}

/**
 * Number of strings that will actually change — one per edited locale value, not
 * per cart item. A single key edited across two locales is two changed strings
 * (two files in the resulting PR), so the cart/banner must count values, not keys.
 */
export function countChanges(items: CartItem[]): number {
  return items.reduce((n, i) => n + Object.keys(i.values).length, 0)
}

export type CartAction =
  | { type: "add"; item: CartItem }
  | { type: "remove"; key: string }
  | { type: "clear" }

/** Pure reducer — dedupes by key (last edit wins). */
export function cartReducer(state: CartItem[], action: CartAction): CartItem[] {
  switch (action.type) {
    case "add":
      return [...state.filter((i) => i.key !== action.item.key), action.item]
    case "remove":
      return state.filter((i) => i.key !== action.key)
    case "clear":
      return []
  }
}

const KEY = "ahead-edit-cart"
let state: CartItem[] = load()
const listeners = new Set<() => void>()

function load(): CartItem[] {
  if (typeof window === "undefined") return []
  try {
    return JSON.parse(window.localStorage.getItem(KEY) ?? "[]")
  } catch {
    return []
  }
}
function persist() {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state))
  } catch {}
}

export function dispatchCart(action: CartAction): void {
  state = cartReducer(state, action)
  persist()
  listeners.forEach((l) => l())
}

/** Synchronous read of the current cart — for code paths that can't use the hook. */
export function getCart(): CartItem[] {
  return state
}

export function useCart(): CartItem[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => state,
    () => []
  )
}
